import type { Node, Edge } from '../../../core/models/scene';
import type { IFrame } from '../../../core/frames/IFrame';
import {
  computeGenogramPersonLabelBounds,
  type ExportShapeLike,
  type PersonLabelGeometryContext,
} from '../../../lib/diagram/personLabelBounds';
import { resolveFiniteNodeRect } from '../../../core/connectors/utils/nodeRect';

export interface ContentBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface ViewportFit {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface ViewportInsets {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export interface ComputeDiagramFitOptions {
  padding?: number;
  /** Upper bound on scale (default 1 — never zoom in beyond 100%). */
  maxScale?: number;
  /**
   * Region of the viewport occluded by fixed chrome (e.g. the assistant
   * panel). Content is scaled to and centered within the *unoccluded* region,
   * so a left-docked panel shifts the fit right instead of hiding it.
   */
  insets?: ViewportInsets;
}

const expandBoundsPoint = (
  bounds: ContentBounds,
  x: number,
  y: number,
): void => {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;
  bounds.minX = Math.min(bounds.minX, x);
  bounds.minY = Math.min(bounds.minY, y);
  bounds.maxX = Math.max(bounds.maxX, x);
  bounds.maxY = Math.max(bounds.maxY, y);
};

const expandBoundsRect = (
  bounds: ContentBounds,
  x: number,
  y: number,
  width: number,
  height: number,
): void => {
  expandBoundsPoint(bounds, x, y);
  expandBoundsPoint(bounds, x + width, y + height);
};

const resolveNodeRect = (node: Node): { x: number; y: number; width: number; height: number } => {
  const { rect } = resolveFiniteNodeRect(node);
  return { x: rect.x, y: rect.y, width: rect.w, height: rect.h };
};

const nodeToExportShapeLike = (node: Node, rect: ReturnType<typeof resolveNodeRect>): ExportShapeLike => {
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const nodeRecord = node as unknown as Record<string, unknown>;
  const rotationRaw = attrs.rotation ?? nodeRecord.rotation;
  return {
    type: typeof node.type === 'string' ? node.type : 'shape',
    position: { x: rect.x, y: rect.y },
    size: { width: rect.width, height: rect.height },
    rotation: typeof rotationRaw === 'number' ? rotationRaw : undefined,
    props: {
      ...nodeRecord,
      name: node.name,
      attrs,
    },
  };
};

const expandFreehandBounds = (bounds: ContentBounds, node: Node): void => {
  if (node.type !== 'freehand') return;
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const points = Array.isArray(attrs.points) ? (attrs.points as number[]) : [];
  const offX = typeof node.x === 'number' ? node.x : 0;
  const offY = typeof node.y === 'number' ? node.y : 0;
  for (let i = 0; i + 1 < points.length; i += 2) {
    expandBoundsPoint(bounds, (points[i] ?? 0) + offX, (points[i + 1] ?? 0) + offY);
  }
};

const expandEdgeBounds = (
  bounds: ContentBounds,
  edges: readonly Edge[],
  nodeById: Map<string, Node>,
): void => {
  for (const edge of edges) {
    const raw = (edge.attrs ?? {}) as Record<string, unknown>;
    const fromPoint = raw.fromPoint as { x?: unknown; y?: unknown } | undefined;
    const toPoint = raw.toPoint as { x?: unknown; y?: unknown } | undefined;
    if (fromPoint && typeof fromPoint.x === 'number' && typeof fromPoint.y === 'number') {
      expandBoundsPoint(bounds, fromPoint.x, fromPoint.y);
    }
    if (toPoint && typeof toPoint.x === 'number' && typeof toPoint.y === 'number') {
      expandBoundsPoint(bounds, toPoint.x, toPoint.y);
    }
    const layoutPoints = raw.layoutPoints as unknown;
    if (Array.isArray(layoutPoints)) {
      for (let i = 0; i + 1 < layoutPoints.length; i += 2) {
        expandBoundsPoint(bounds, layoutPoints[i] as number, layoutPoints[i + 1] as number);
      }
    }
    const fromId = typeof edge.from === 'string' ? edge.from : '';
    const toId = typeof edge.to === 'string' ? edge.to : '';
    if (fromId && !fromId.startsWith('__free')) {
      const fromNode = nodeById.get(fromId);
      if (fromNode) {
        const rect = resolveNodeRect(fromNode);
        expandBoundsRect(bounds, rect.x, rect.y, rect.width, rect.height);
      }
    }
    if (toId && !toId.startsWith('__free')) {
      const toNode = nodeById.get(toId);
      if (toNode) {
        const rect = resolveNodeRect(toNode);
        expandBoundsRect(bounds, rect.x, rect.y, rect.width, rect.height);
      }
    }
  }
};

export const computeDiagramContentBounds = (input: {
  nodes: readonly Node[];
  edges: readonly Edge[];
  frames?: readonly IFrame[];
  labelGeometry?: PersonLabelGeometryContext;
}): ContentBounds | null => {
  const bounds: ContentBounds = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };

  const nodeById = new Map(input.nodes.map((node) => [node.id, node]));

  for (const node of input.nodes) {
    const rect = resolveNodeRect(node);
    expandBoundsRect(bounds, rect.x, rect.y, rect.width, rect.height);
    expandFreehandBounds(bounds, node);

    const exportShape = nodeToExportShapeLike(node, rect);
    if (input.labelGeometry) {
      exportShape.props.__exportLine2Field = input.labelGeometry.line2Field;
    }
    const labelBounds = computeGenogramPersonLabelBounds(exportShape);
    if (labelBounds) {
      expandBoundsRect(bounds, labelBounds.minX, labelBounds.minY, labelBounds.maxX - labelBounds.minX, labelBounds.maxY - labelBounds.minY);
    }
  }

  expandEdgeBounds(bounds, input.edges, nodeById);

  for (const frame of input.frames ?? []) {
    if (frame.visible === false) continue;
    expandBoundsRect(bounds, frame.bounds.x, frame.bounds.y, frame.bounds.width, frame.bounds.height);
  }

  if (
    !Number.isFinite(bounds.minX) ||
    !Number.isFinite(bounds.minY) ||
    !Number.isFinite(bounds.maxX) ||
    !Number.isFinite(bounds.maxY)
  ) {
    return null;
  }

  return bounds;
};

export const fitBoundsToViewport = (
  bounds: ContentBounds,
  viewport: { width: number; height: number },
  options: ComputeDiagramFitOptions = {},
): ViewportFit | null => {
  const { width: containerWidth, height: containerHeight } = viewport;
  if (containerWidth <= 0 || containerHeight <= 0) return null;

  const insetLeft = Math.max(0, options.insets?.left ?? 0);
  const insetRight = Math.max(0, options.insets?.right ?? 0);
  const insetTop = Math.max(0, options.insets?.top ?? 0);
  const insetBottom = Math.max(0, options.insets?.bottom ?? 0);
  // Fall back to the full viewport if insets would leave no usable region.
  const usableWidth = containerWidth - insetLeft - insetRight;
  const usableHeight = containerHeight - insetTop - insetBottom;
  const regionWidth = usableWidth > 1 ? usableWidth : containerWidth;
  const regionHeight = usableHeight > 1 ? usableHeight : containerHeight;
  const regionCenterX = usableWidth > 1 ? insetLeft + usableWidth / 2 : containerWidth / 2;
  const regionCenterY = usableHeight > 1 ? insetTop + usableHeight / 2 : containerHeight / 2;

  const padding = options.padding ?? 80;
  const paddedMinX = bounds.minX - padding;
  const paddedMinY = bounds.minY - padding;
  const paddedMaxX = bounds.maxX + padding;
  const paddedMaxY = bounds.maxY + padding;

  const contentWidth = Math.max(paddedMaxX - paddedMinX, 1);
  const contentHeight = Math.max(paddedMaxY - paddedMinY, 1);

  const fitScaleRaw = Math.min(regionWidth / contentWidth, regionHeight / contentHeight);
  const fitScale = Math.min(2, Math.max(0.05, fitScaleRaw));

  const maxScale = options.maxScale ?? 1;
  const targetScale = Math.min(fitScale, maxScale);

  const centerWorldX = (paddedMinX + paddedMaxX) / 2;
  const centerWorldY = (paddedMinY + paddedMaxY) / 2;

  return {
    scale: targetScale,
    offsetX: regionCenterX - centerWorldX * targetScale,
    offsetY: regionCenterY - centerWorldY * targetScale,
  };
};

export const computeDiagramFitToContent = (
  input: {
    nodes: readonly Node[];
    edges: readonly Edge[];
    frames?: readonly IFrame[];
    labelGeometry?: PersonLabelGeometryContext;
  },
  viewport: { width: number; height: number },
  options: ComputeDiagramFitOptions = {},
): ViewportFit | null => {
  const bounds = computeDiagramContentBounds(input);
  if (!bounds) return null;
  return fitBoundsToViewport(bounds, viewport, options);
};
